import React from "react";
import { motion } from "framer-motion";
import {
  FaGithub,
  FaBook,
  FaUsers,
  FaShieldAlt,
  FaRocket,
  FaChartLine,
} from "react-icons/fa";
import { HiOutlineExternalLink, HiCode, HiDatabase } from "react-icons/hi";

const VXDFSection = () => {
  const features = [
    {
      icon: HiDatabase,
      title: "Standardized Format",
      description:
        "JSON-based format for documenting confirmed security vulnerabilities with structured evidence",
    },
    {
      icon: FaShieldAlt,
      title: "Evidence-Backed",
      description:
        "Every vulnerability includes proof of exploitability - eliminating false positives",
    },
    {
      icon: HiCode,
      title: "Developer Friendly",
      description:
        "Machine-readable format that integrates seamlessly with existing security tooling",
    },
  ];

  const stats = [
    {
      icon: FaGithub,
      label: "Open Source",
      value: "100%",
      description: "Community-driven development",
    },
    {
      icon: FaUsers,
      label: "Growing Community",
      value: "Active",
      description: "Contributors worldwide",
    },
    {
      icon: FaRocket,
      label: "Industry Adoption",
      value: "Early Stage",
      description: "Gaining traction in security community",
    },
  ];

  return (
    <section className="relative py-20 bg-vxdf-dark overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-vxdf-primary/10 text-vxdf-accent px-4 py-2 rounded-full mb-4">
            <FaRocket className="text-sm" />
            <span className="text-sm font-medium">
              Leading Open Source Initiative
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            VXDF - Validated Exploitable Data Flow
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Creating the industry standard for documenting confirmed security
            vulnerabilities with structured evidence. VXDF eliminates false
            positives and improves security workflows across the industry.
          </p>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {features.map((feature, index) => (
            <motion.div
              key={index}
              whileHover={{ y: -5, scale: 1.02 }}
              className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300"
            >
              <div className="w-14 h-14 bg-vxdf-primary/20 rounded-lg flex items-center justify-center mb-4">
                <feature.icon className="text-2xl text-vxdf-accent" />
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">
                {feature.title}
              </h3>
              <p className="text-gray-300">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="grid md:grid-cols-3 gap-8 mb-16"
        >
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-vxdf-primary/20 rounded-full mb-4">
                <stat.icon className="text-2xl text-vxdf-accent" />
              </div>
              <div className="text-3xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-sm font-medium text-vxdf-accent mb-1">
                {stat.label}
              </div>
              <div className="text-sm text-gray-400">{stat.description}</div>
            </div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://vxdf.org"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-vxdf-primary hover:bg-vxdf-secondary text-white px-8 py-4 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105"
            >
              Explore VXDF
              <HiOutlineExternalLink className="text-xl" />
            </a>
            <a
              href="https://github.com/mihir-shah99/vxdf"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-lg font-semibold transition-all duration-300"
            >
              <FaGithub className="text-xl" />
              View on GitHub
            </a>
          </div>
          <p className="mt-6 text-gray-400">
            Join the community building the future of vulnerability reporting
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default VXDFSection;
